/**
 * CX-3744: tracks whether the visitor has actively interacted with the current
 * page since load or the last route change (the latch is cleared on navigation
 * by useResetInteractionOnRouteChange). The session-expired dialog should only
 * interrupt someone who is actually using the app — the many bootstrap requests
 * that fire on mount
 * (/v0/user-info, /v0/hierarchy, /color-effect, /feature-flags, ...) can 401
 * on a returning visitor with an expired stored token and must stay silent.
 * A genuine user action (a request that 401s after a click/keypress) still
 * opens the dialog (CX-1948).
 */

let userHasInteracted = false;
let listenersAttached = false;

const markInteracted = (): void => {
  userHasInteracted = true;
};

const attachListeners = (): void => {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;
  for (const eventName of ["pointerdown", "keydown"] as const) {
    window.addEventListener(eventName, markInteracted, {
      capture: true,
      passive: true,
    });
  }
};

attachListeners();

/**
 * True once the visitor has produced a real pointer/keyboard interaction since
 * page load or the last route change. Used to distinguish user-driven auth
 * failures from automatic on-mount ones.
 */
export const hasUserInteracted = (): boolean => userHasInteracted;

/**
 * Clear the interaction latch. Called on every route change
 * (useResetInteractionOnRouteChange) so a freshly mounted page's on-mount
 * bootstrap 401s are treated as passive detection until the user interacts with
 * that page — otherwise an earlier click on a public link keeps the latch armed
 * and the next page's bootstrap 401 wrongly pops the login dialog. Also used to
 * reset state between tests.
 */
export const resetUserInteraction = (): void => {
  userHasInteracted = false;
};
